import {
  Button,
  FormInput,
  FormPassword,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import { AvatarPresignedUpload } from "@/shared/ui/AvatarPresignedUpload";

const ProfileFormSchema = z
  .object({
    name: z.string().min(1, "ຕ້ອງໃສ່ຊື່"),
    phone: z.string().max(32).optional(),
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
    imageKey: z.string().optional().nullable(),
  })
  .superRefine((val, ctx) => {
    if (val.password) {
      if (val.password.length < 6) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ລະຫັດຜ່ານຕ້ອງມີຢ່າງໜ້ອຍ 6 ຕົວອັກສອນ",
          path: ["password"],
        });
      }

      if (!val.confirmPassword || val.confirmPassword.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງຢືນຢັນລະຫັດຜ່ານ",
          path: ["confirmPassword"],
        });
      } else if (val.confirmPassword !== val.password) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ລະຫັດຜ່ານບໍ່ກົງກັນ",
          path: ["confirmPassword"],
        });
      }
    }
  });

export type ProfileFormValues = z.infer<typeof ProfileFormSchema>;

export function ProfileForm({
  initialValues,
  submitting,
  onSubmit,
}: {
  initialValues?: Partial<ProfileFormValues>;
  submitting?: boolean;
  onSubmit: (values: {
    name: string;
    phone?: string;
    password?: string;
    confirmPassword?: string;
    imageKey?: string | null;
  }) => void;
}) {
  const methods = RHF.useForm<ProfileFormValues>({
    resolver: zodResolver(ProfileFormSchema),
    defaultValues: {
      name: "",
      phone: "",
      password: "",
      confirmPassword: "",
      imageKey: null,
      ...initialValues,
    },
  });

  return (
    <FormRoot<ProfileFormValues>
      methods={methods}
      onSubmit={(vals) =>
        onSubmit({
          name: vals.name,
          phone: vals.phone?.trim() ?? "",
          password: vals.password || undefined,
          confirmPassword: vals.confirmPassword || undefined,
          imageKey: vals.imageKey ?? undefined,
        })
      }
      className="space-y-4"
    >
      <RHF.Controller
        control={methods.control}
        name="imageKey"
        render={({ field }) => (
          <AvatarPresignedUpload
            value={field.value}
            onChange={field.onChange}
            keyPrefix="uploads/avatars"
            label="ຮູບໂປຣໄຟລ໌"
            hint="ອັບໂຫຼດຮູບໃໝ່"
          />
        )}
      />

      <FormInput name="name" label="ຊື່" requiredMark placeholder="John Doe" />
      <FormInput
        name="phone"
        type="tel"
        label="ເບີໂທລະສັບ"
        placeholder="020xxxxxxxx"
      />
      {/* Email read-only — shown on ProfilePage */}
      <FormPassword name="password" label="ລະຫັດຜ່ານໃໝ່" placeholder="********" />
      <FormPassword
        name="confirmPassword"
        label="ຢືນຢັນລະຫັດຜ່ານ"
        placeholder="********"
      />

      <div className="flex justify-end gap-2">
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກການປ່ຽນແປງ
        </Button>
      </div>
    </FormRoot>
  );
}
